import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';
import { getStageLabel } from '../constants/dealStages';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

function dealSearchBlob(deal) {
  return `${deal.project_name || ''} ${deal.client_name || ''} ${deal.stage || ''} ${deal.id || ''}`.toLowerCase();
}

export function BexioReconDealPicker({
  deals,
  value,
  onChange,
  disabled,
  hintText,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => deals.find((d) => d.id === value) || null,
    [deals, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return deals.slice(0, 80);
    return deals.filter((d) => dealSearchBlob(d).includes(q)).slice(0, 80);
  }, [deals, query]);

  const label = selected
    ? `${selected.project_name || 'Untitled'}${selected.client_name ? ` · ${selected.client_name}` : ''}`
    : 'Select deal…';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-7 min-w-0 flex-1 justify-between px-2 text-[11px] font-normal"
          data-testid="bexio-recon-deal-picker"
          title={hintText || 'Search by project or client'}
        >
          <span className={cn('truncate', !selected && 'text-gray-400')}>{label}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[80] w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search project or client…"
            value={query}
            onValueChange={setQuery}
            data-testid="bexio-recon-deal-search"
          />
          <CommandList>
            <CommandEmpty>No matching deals.</CommandEmpty>
            <CommandGroup>
              {filtered.map((deal) => (
                <CommandItem
                  key={deal.id}
                  value={deal.id}
                  onSelect={() => {
                    onChange(deal.id);
                    setOpen(false);
                    setQuery('');
                  }}
                  data-testid={`bexio-recon-deal-option-${deal.id}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5 shrink-0',
                      value === deal.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-xs font-medium text-gray-900">
                      {deal.project_name || 'Untitled'}
                    </span>
                    <span className="truncate text-[10px] text-gray-500">
                      {deal.client_name || 'No client'} · {getStageLabel(deal.stage) || deal.stage || '—'}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
